import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BACKEND_URL, getUploadUrl } from '../config';
import { ShoppingCart, Shirt, Laptop, Sparkles, Utensils, Home, Activity, Pill, Wrench, Palette, Package, Store, Search, X, ShoppingBag, User } from 'lucide-react';

// Composant Image avec gestion d'erreur
const ShopImage = ({ src, alt, className, fallbackIcon, fallbackGradient }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  if (!src || hasError) {
    return (
      <div className={`${className} ${fallbackGradient} flex items-center justify-center`}>
        <span className="text-4xl opacity-50">{fallbackIcon}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} ${isLoaded ? '' : 'opacity-0'} transition-opacity duration-300`}
      onError={() => setHasError(true)}
      onLoad={() => setIsLoaded(true)}
    />
  );
};

const ShopsDirectory = () => {
  const [shops, setShops] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [displayedShops, setDisplayedShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('random');
  const [hasMore, setHasMore] = useState(true);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const shopsPerLoad = 12;
  
  const observerRef = useRef();
  
  // Fonction de normalisation pour la recherche
  const normalizeText = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ');
  };

  // Fonction de mélange aléatoire (Fisher-Yates)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  // Charger plus de boutiques (scroll infini)
  const loadMoreShops = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      const currentLength = displayedShops.length;
      const nextShops = filteredShops.slice(currentLength, currentLength + shopsPerLoad);
      
      if (nextShops.length > 0) {
        setDisplayedShops(prev => [...prev, ...nextShops]);
        setHasMore(currentLength + nextShops.length < filteredShops.length);
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
    }, 300);
  }, [loadingMore, hasMore, displayedShops.length, filteredShops, shopsPerLoad]);
  
  // Observer pour le scroll infini
  const lastShopRef = useCallback(node => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreShops();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, loadMoreShops]);

  // Charger les boutiques
  useEffect(() => {
    loadShops();
  }, []);

  // Filtrer et trier les boutiques
  useEffect(() => {
    let filtered = [...shops];

    // Recherche
    if (search) {
      const searchTerms = normalizeText(search).split(/\s+/).filter(t => t.length > 0);
      filtered = filtered.filter(shop => {
        const shopName = normalizeText(shop.nom);
        const shopDesc = normalizeText(shop.description);
        const ownerName = normalizeText(shop.owner_name);
        const category = normalizeText(shop.category);
        const allFields = `${shopName} ${shopDesc} ${ownerName} ${category}`;
        
        return searchTerms.every(term => allFields.includes(term));
      });
    }

    // Filtre par catégorie
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(shop => shop.category === categoryFilter);
    }

    // Tri
    if (sortBy === 'random') {
      filtered = shuffleArray(filtered);
    } else if (sortBy === 'recent') {
      filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
    } else if (sortBy === 'products') {
      filtered.sort((a, b) => (b.products_count || 0) - (a.products_count || 0));
    }

    setFilteredShops(filtered);
    setDisplayedShops(filtered.slice(0, shopsPerLoad));
    setHasMore(filtered.length > shopsPerLoad);
  }, [shops, search, categoryFilter, sortBy]);

  const loadShops = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/shops/directory`);
      const data = await res.json();
      
      if (data.success) {
        setShops(data.shops || []);
        
        // Extraire les catégories disponibles
        const categories = [...new Set(data.shops.map(s => s.category).filter(Boolean))];
        setAvailableCategories(categories);
      } else {
        console.error('Erreur chargement boutiques:', data.message);
        setShops([]);
      }
    } catch (err) {
      console.error('Erreur chargement boutiques:', err);
      setShops([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryInfo = (cat) => {
    const categories = {
      commerce: { icon: <ShoppingCart className="w-4 h-4 inline" />, label: 'Commerce' },
      mode: { icon: <Shirt className="w-4 h-4 inline" />, label: 'Mode & Vêtements' },
      tech: { icon: <Laptop className="w-4 h-4 inline" />, label: 'Tech & Électronique' },
      beaute: { icon: <Sparkles className="w-4 h-4 inline" />, label: 'Beauté & Cosmétiques' },
      alimentation: { icon: <Utensils className="w-4 h-4 inline" />, label: 'Alimentation' },
      maison: { icon: <Home className="w-4 h-4 inline" />, label: 'Maison & Déco' },
      sport: { icon: <Activity className="w-4 h-4 inline" />, label: 'Sport & Loisirs' },
      sante: { icon: <Pill className="w-4 h-4 inline" />, label: 'Santé & Bien-être' },
      services: { icon: <Wrench className="w-4 h-4 inline" />, label: 'Services' },
      artisanat: { icon: <Palette className="w-4 h-4 inline" />, label: 'Artisanat' },
      autre: { icon: <Package className="w-4 h-4 inline" />, label: 'Autre' }
    };
    return categories[cat] || categories.autre;
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 mb-4"></div>
        <p className="text-indigo-600 font-medium">Chargement des boutiques...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 mb-4 transition">
            ← Retour au feed
          </Link>
          
          <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl p-6 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnYtNGgydjRoNHYyaC00djRoLTJ2LTR6bTAtMzBoLTJ2LTRoMnYtNGgydjRoNHYyaC00djRoLTJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
            <div className="relative flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center"><Store className="w-8 h-8 text-white" /></div>
                <div className="text-white">
                  <h1 className="text-2xl md:text-3xl font-bold">Boutiques DJAMANN</h1>
                  <p className="text-white/80">Découvrez les boutiques de notre communauté</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Link 
                  to="/workspace/shop"
                  className="inline-flex items-center justify-center gap-2 bg-white text-teal-600 font-semibold px-5 py-2.5 rounded-xl hover:bg-teal-50 hover:scale-105 transition-all shadow-lg"
                >
                  <Sparkles className="w-5 h-5" />
                  <span>Ouvrir ma boutique en ligne</span>
                </Link>
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur rounded-xl px-4 py-2">
                  <span className="text-white/80 text-sm">Total:</span>
                  <span className="text-white font-bold text-xl">{shops.length}</span>
                  <span className="text-white/80 text-sm">boutiques</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Barre de recherche sticky */}
        <div className="sticky top-16 z-30 -mx-4 px-4 md:mx-0 md:px-0 py-2 bg-gradient-to-b from-indigo-50/95 via-purple-50/95 to-transparent">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-lg p-3 md:p-4 border border-teal-100">
            <div className="flex gap-2">
              {/* Recherche */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all"
                />
                {search && (
                  <button 
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Bouton filtres */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-medium transition-all ${
                  showFilters || search || categoryFilter !== 'all'
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="hidden sm:inline">Filtres</span>
                {(search || categoryFilter !== 'all') && (
                  <span className="bg-white text-teal-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {(search ? 1 : 0) + (categoryFilter !== 'all' ? 1 : 0)}
                  </span>
                )}
              </button>

              {/* Tri - caché sur mobile */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="hidden sm:block px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
              >
                <option value="random">⟳ Aléatoire</option>
                <option value="recent">↓ Récentes</option>
                <option value="oldest">↑ Anciennes</option>
                <option value="name">A-Z</option>
                <option value="products">◻ Produits</option>
              </select>
            </div>

            {/* Panneau de filtres dépliable */}
            {showFilters && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                {/* Catégories */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Catégorie</label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setCategoryFilter('all')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        categoryFilter === 'all'
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Toutes
                    </button>
                    {availableCategories.map(cat => {
                      const catInfo = getCategoryInfo(cat);
                      return (
                        <button
                          key={cat}
                          onClick={() => setCategoryFilter(cat)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            categoryFilter === cat
                              ? 'bg-emerald-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {catInfo.icon} {catInfo.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Tri - visible sur mobile */}
                <div className="sm:hidden">
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Tri</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
                  >
                    <option value="random">⟳ Aléatoire</option>
                    <option value="recent">↓ Plus récentes</option>
                    <option value="oldest">↑ Plus anciennes</option>
                    <option value="name">A-Z Nom A-Z</option>
                    <option value="products">◻ Plus de produits</option>
                  </select>
                </div>

                {/* Reset */}
                {(search || categoryFilter !== 'all') && (
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-gray-500">
                      <span className="font-semibold text-teal-600">{filteredShops.length}</span> résultat(s)
                    </span>
                    <button
                      onClick={() => { setSearch(''); setCategoryFilter('all'); }}
                      className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                    >
                      Effacer les filtres
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Filtres actifs - badges compacts */}
            {!showFilters && (search || categoryFilter !== 'all') && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {search && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs">
                    "{search.substring(0, 12)}{search.length > 12 ? '...' : ''}"
                    <button onClick={() => setSearch('')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {categoryFilter !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                    {getCategoryInfo(categoryFilter).icon} {getCategoryInfo(categoryFilter).label}
                    <button onClick={() => setCategoryFilter('all')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                <button
                  onClick={() => { setSearch(''); setCategoryFilter('all'); }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Effacer
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Résultats */}
        <div className="flex items-center justify-between mb-4 mt-4">
          <p className="text-gray-600">
            {filteredShops.length === shops.length ? (
              <>{shops.length} boutique{shops.length > 1 ? 's' : ''}</>
            ) : (
              <>{filteredShops.length} résultat{filteredShops.length > 1 ? 's' : ''} sur {shops.length}</>
            )}
          </p>
        </div>

        {/* Grille des boutiques */}
        {displayedShops.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-12 text-center">
            <Store className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Aucune boutique trouvée</h3>
            <p className="text-gray-600 mb-4">
              {search || categoryFilter !== 'all' 
                ? 'Essayez de modifier vos critères de recherche'
                : 'Aucune boutique n\'est encore active sur DJAMANN'}
            </p>
            {(search || categoryFilter !== 'all') && (
              <button
                onClick={() => { setSearch(''); setCategoryFilter('all'); }}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayedShops.map((shop, index) => {
              const isLast = index === displayedShops.length - 1;
              const catInfo = getCategoryInfo(shop.category);
              
              return (
                <Link
                  key={shop.id}
                  to={`/boutique/${shop.slug}`}
                  ref={isLast ? lastShopRef : null}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden hover:scale-[1.02]"
                >
                  {/* Banner */}
                  <div className="relative h-32 overflow-hidden">
                    <ShopImage
                      src={getUploadUrl(shop.banner)}
                      alt={shop.nom}
                      className="w-full h-full object-cover"
                      fallbackIcon={<Store className="w-10 h-10 text-white/50" />}
                      fallbackGradient="bg-gradient-to-br from-teal-400 to-emerald-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    
                    {/* Badge catégorie */}
                    {shop.category && (
                      <div className="absolute top-2 right-2 px-2 py-1 bg-white/90 backdrop-blur rounded-lg text-xs font-medium">
                        {catInfo.icon} {catInfo.label}
                      </div>
                    )}
                    
                    {/* Logo */}
                    <div className="absolute -bottom-8 left-4">
                      <div className="w-16 h-16 rounded-xl border-4 border-white shadow-lg overflow-hidden bg-white">
                        <ShopImage
                          src={getUploadUrl(shop.logo)}
                          alt={shop.nom}
                          className="w-full h-full object-cover"
                          fallbackIcon={<ShoppingBag className="w-6 h-6 text-indigo-400" />}
                          fallbackGradient="bg-gradient-to-br from-indigo-100 to-purple-100"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Contenu */}
                  <div className="pt-10 p-4">
                    <h3 className="font-bold text-gray-800 text-lg mb-1 group-hover:text-teal-600 transition line-clamp-1">
                      {shop.nom || 'Boutique sans nom'}
                    </h3>
                    
                    {/* Vendeur */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                        <ShopImage
                          src={getUploadUrl(shop.owner_avatar)}
                          alt={shop.owner_name}
                          className="w-full h-full object-cover"
                          fallbackIcon={<User className="w-4 h-4 text-gray-400" />}
                          fallbackGradient="bg-gradient-to-br from-gray-100 to-gray-200"
                        />
                      </div>
                      <span className="text-sm text-gray-500 truncate">
                        {shop.owner_name || 'Vendeur'}
                      </span>
                    </div>
                    
                    {/* Description */}
                    {shop.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {shop.description}
                      </p>
                    )}
                    
                    {/* Stats */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Package className="w-4 h-4" />
                        <span>{shop.products_count || 0} produit{(shop.products_count || 0) > 1 ? 's' : ''}</span>
                      </div>
                      
                      <span className="text-teal-600 font-medium text-sm group-hover:translate-x-1 transition-transform">
                        Visiter →
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Loader scroll infini */}
        {loadingMore && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-teal-600"></div>
          </div>
        )}

        {/* Message fin de liste */}
        {!hasMore && displayedShops.length > 0 && (
          <div className="text-center py-8 text-gray-500">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-teal-500" />
            <p>Vous avez vu toutes les boutiques !</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopsDirectory;
